import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function CartPage() {
  const { t } = useTranslation();
  const { isAuthenticated, openAuthModal } = useAuth();
  const { cart, updateQuantity, removeFromCart, loading } = useCart();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center" data-testid="cart-page">
        <ShoppingBag className="h-16 w-16 text-brand-gold/50 mb-4" />
        <h1 className="font-heading text-3xl text-brand-black mb-4">{t('cart.yourCart')}</h1>
        <p className="text-brand-dark/60 mb-6">{t('cart.loginToView')}</p>
        <Button onClick={() => openAuthModal('login')} className="bg-brand-gold text-white hover:bg-brand-gold">
          {t('nav.login')}
        </Button>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center" data-testid="cart-page-empty">
        <ShoppingBag className="h-16 w-16 text-brand-gold/50 mb-4" />
        <h1 className="font-heading text-3xl text-brand-black mb-4">{t('cart.emptyCart')}</h1>
        <p className="text-brand-dark/60 mb-6">{t('cart.emptyCartDesc')}</p>
        <Link to="/products">
          <Button className="bg-brand-gold text-white hover:bg-brand-gold">
            {t('cart.continueShopping')}
          </Button>
        </Link>
      </div>
    );
  }

  const handleUpdateQuantity = async (productId, newQuantity) => {
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Produit retiré du panier');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-accent text-brand-gold text-sm tracking-[0.3em] uppercase">{t('cart.yourSelection')}</span>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-brand-black mt-2 mb-12">{t('cart.yourCart')}</h1>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item, idx) => (
              <motion.div
                key={item.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-brand-cream border border-brand-gold/20 rounded-lg p-6 flex gap-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                <Link to={`/products/${item.product_id}`} className="w-24 h-24 flex-shrink-0 overflow-hidden rounded">
                  <img 
                    src={item.image || 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=200'} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </Link>
                
                <div className="flex-1">
                  <Link to={`/products/${item.product_id}`} className="font-heading text-lg text-brand-black hover:text-brand-gold transition-colors">
                    {item.name}
                  </Link>
                  <p className="text-brand-gold font-semibold mt-1">${item.price.toFixed(2)} CAD</p>
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-brand-gold/20 rounded">
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                        className="p-2 text-brand-dark/60 hover:text-brand-black transition-colors"
                        disabled={loading}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-12 text-center text-brand-black">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                        className="p-2 text-brand-dark/60 hover:text-brand-black transition-colors"
                        disabled={loading}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="text-brand-black font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                      <button
                        onClick={() => handleRemove(item.product_id)}
                        className="text-brand-dark/50 hover:text-brand-gold transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-brand-cream border border-brand-gold/20 rounded-lg p-6 sticky top-28">
              <h2 className="font-heading text-2xl text-brand-black mb-6">{t('cart.summary')}</h2>
              
              <div className="space-y-4 border-b border-brand-gold/20 pb-6">
                <div className="flex justify-between text-brand-dark/80">
                  <span>{t('cart.subtotal')}</span>
                  <span>${cart.total.toFixed(2)} CAD</span>
                </div>
                <div className="flex justify-between text-brand-dark/80">
                  <span>{t('cart.shipping')}</span>
                  <span>{t('cart.calculatedNext')}</span>
                </div>
              </div>
              
              <div className="flex justify-between py-6 text-xl">
                <span className="font-semibold text-brand-black">{t('cart.total')}</span>
                <span className="font-bold text-brand-gold" data-testid="cart-total">${cart.total.toFixed(2)} CAD</span>
              </div>
              
              <Link to="/checkout">
                <Button className="w-full bg-brand-gold text-white hover:bg-brand-gold py-6 font-bold uppercase tracking-wider btn-shine rounded" data-testid="checkout-btn">
                  {t('cart.checkout')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              
              <Link to="/products" className="block text-center mt-4 text-brand-dark/60 hover:text-brand-gold transition-colors text-sm">
                {t('cart.continueShopping')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
